/**
 * Små gränssnittsvärden som ska överleva en omladdning i SAMMA flik (sessionStorage). Ingen
 * kalenderdata, inga lösenord, inga tokens.
 *  - Var användaren var (vy, kalendervy, datum) – återställs efter inloggning/utloggning.
 *  - Att en icke-ihågkommen session lever i denna flik (skiljer omladdning från ny webbläsarsession).
 *  - Ett meddelande att visa efter omladdningen.
 * Endast interna hash-vägar godtas: externa omdirigeringsadresser kan aldrig följas.
 */
const RETURN_KEY = 'autoday.returnTo';
const LIVE_KEY = 'autoday.liveSession';
const NOTICE_KEY = 'autoday.notice';

export type CalendarViewKind = 'month' | 'week' | 'day' | 'agenda';
const ROUTE_RE = /^#\/(idag|kalender|viktigt|kalendrar|import|installningar|notiser)$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const VIEWS: CalendarViewKind[] = ['month', 'week', 'day', 'agenda'];

let currentView: CalendarViewKind | null = null;
let currentFocus: string | null = null;

export function noteCalendarView(v: CalendarViewKind) {
  currentView = v;
}
export function noteFocusDate(d: string) {
  currentFocus = d;
}

function ss(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export interface ReturnTo {
  hash: string;
  focusDate: string | null;
  calendarView: CalendarViewKind | null;
}

export function saveReturnTo(): void {
  const hash = ROUTE_RE.test(location.hash) ? location.hash : '#/idag';
  ss()?.setItem(RETURN_KEY, JSON.stringify({ hash, focusDate: currentFocus, calendarView: currentView }));
}

/** Validerar strikt – allt som inte är en känd intern väg blir #/idag. */
export function parseReturnTo(raw: string | null): ReturnTo | null {
  if (!raw) return null;
  let v: Partial<ReturnTo>;
  try {
    v = JSON.parse(raw) as Partial<ReturnTo>;
  } catch {
    return null;
  }
  return {
    hash: typeof v.hash === 'string' && ROUTE_RE.test(v.hash) ? v.hash : '#/idag',
    focusDate: typeof v.focusDate === 'string' && DATE_RE.test(v.focusDate) ? v.focusDate : null,
    calendarView: VIEWS.includes(v.calendarView as CalendarViewKind) ? (v.calendarView as CalendarViewKind) : null,
  };
}

export function consumeReturnTo(): ReturnTo | null {
  const s = ss();
  const raw = s?.getItem(RETURN_KEY) ?? null;
  s?.removeItem(RETURN_KEY);
  return parseReturnTo(raw);
}

export function setLiveSession(profileId: string) {
  ss()?.setItem(LIVE_KEY, profileId);
}
export function getLiveSession(): string | null {
  return ss()?.getItem(LIVE_KEY) ?? null;
}
export function clearLiveSession() {
  ss()?.removeItem(LIVE_KEY);
}

export function setNotice(text: string) {
  ss()?.setItem(NOTICE_KEY, text.slice(0, 300));
}
export function consumeNotice(): string | null {
  const s = ss();
  const v = s?.getItem(NOTICE_KEY) ?? null;
  s?.removeItem(NOTICE_KEY);
  return v;
}
